import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { RootState } from '../../store/store';
import { Drone } from '../../types/drone';
import { renewCommands } from '../../utils/credentials';
import { ApiError } from '../../types/api';
import { DroneRegistration } from '../../types/registration';
import { droneService } from '../../services/droneService';
import { formatTimestamp } from '../../utils/formatters';

type RecordState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'none' }
  | { kind: 'found'; registration: DroneRegistration };

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * A physical drone's registration record, and its key on request (#411's
 * addendum, #649).
 *
 * The record comes from the list, which never decrypts a key, so opening the
 * card reads no credential. The key is fetched only when the pilot asks, and
 * that read is audited on the server. It lives in this component's state and
 * nowhere else: not in the Redux store, not in browser storage, not in a log.
 * Closing the card unmounts this, and the key with it.
 *
 * A virtual drone has no registration, so the section is absent for one.
 */
export const DroneRegistrationSection: React.FC<{ drone: Drone }> = ({ drone }) => {
  const subject = useSelector((state: RootState) => state.user.profile?.subject);
  const [record, setRecord] = useState<RecordState>({ kind: 'loading' });
  const [key, setKey] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (drone.virtual) return;
    let active = true;
    droneService
      .getRegistration(drone.id)
      .then((registration) => {
        if (!active) return;
        setRecord(registration ? { kind: 'found', registration } : { kind: 'none' });
      })
      .catch(() => {
        if (active) setRecord({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [drone.id, drone.virtual]);

  if (drone.virtual) {
    return null;
  }

  const reveal = async () => {
    setRevealing(true);
    setRevealError(null);
    try {
      setKey(await droneService.revealRegistrationKey(drone.id));
    } catch (err: unknown) {
      // 404 is both "no registration" and "not yours", worded the same on
      // purpose by the server; this says no more than it does.
      setRevealError(
        (err as ApiError)?.status === 404
          ? 'No registration key was found for this drone.'
          : 'The registration key could not be fetched. Try again.'
      );
    } finally {
      setRevealing(false);
    }
  };

  const copy = async () => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
    } catch {
      setRevealError('Copying was refused by the browser. Select the key and copy it instead.');
    }
  };

  return (
    <Box component="section" aria-labelledby={`registration-${drone.id}`}>
      <Typography id={`registration-${drone.id}`} variant="subtitle2" component="h3" gutterBottom>
        Registration
      </Typography>

      {record.kind === 'loading' && <CircularProgress size={20} aria-label="Loading the registration" />}
      {record.kind === 'failed' && (
        <Alert severity="warning">The registration record could not be loaded.</Alert>
      )}
      {record.kind === 'none' && (
        <Alert severity="info">
          This drone has no registration record. Drones enrolled before registration keys were
          kept, or created with the admin API key, have none.
        </Alert>
      )}
      {record.kind === 'found' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Field label="Hardware ID">{record.registration.hardware_id}</Field>
          <Field label="Enrolled">
            {formatTimestamp(record.registration.enrolled_at)}, by{' '}
            {record.registration.enrolled_by === subject ? 'you' : 'another pilot'}
          </Field>
          <Field label="Enrolled against">{record.registration.api_url}</Field>

          <Typography variant="body2" color="text.secondary">
            The registration key can be shown here again whenever you need it, and every time it
            is shown is recorded. The drone&apos;s access and refresh tokens are different: they
            were shown once, at enrolment, and cannot be shown again. To replace them, run{' '}
            <code>{renewCommands(drone)[0]}</code>, which installs new ones on the drone.
          </Typography>

          {key === null ? (
            <Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={revealing ? <CircularProgress size={16} /> : <Visibility />}
                onClick={() => void reveal()}
                disabled={revealing}
              >
                Show registration key
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                aria-label="Registration key"
                sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', flexGrow: 1 }}
              >
                {key}
              </Typography>
              <Tooltip title={copied ? 'Copied' : 'Copy'}>
                <IconButton size="small" onClick={() => void copy()} aria-label="Copy registration key">
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Hide">
                <IconButton
                  size="small"
                  onClick={() => {
                    setKey(null);
                    setCopied(false);
                  }}
                  aria-label="Hide registration key"
                >
                  <VisibilityOff fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {revealError && <Alert severity="error">{revealError}</Alert>}
        </Box>
      )}
    </Box>
  );
};
